/**
 * VendorPortalLayout — sidebar shell for /store/vendor/*.
 * Guards: must be signed in, must have a store_vendors row.
 * If no vendor row → redirects to /store/vendor-setup.
 */
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useVendor } from "@/lib/store/useVendor";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StoreSubnav from "@/components/store/StoreSubnav";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Undo2,
  Wallet,
  Settings,
  Sparkles,
  Store,
} from "lucide-react";

const navItems = [
  { to: "/store/vendor", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/store/vendor/products", label: "Products", icon: Package },
  { to: "/store/vendor/orders", label: "Orders", icon: ShoppingBag },
  { to: "/store/vendor/refunds", label: "Refunds", icon: Undo2 },
  { to: "/store/vendor/payouts", label: "Payouts", icon: Wallet },
  { to: "/store/vendor/settings", label: "Settings", icon: Settings },
];

const VendorPortalLayout = () => {
  const { user, loading: authLoading } = useAuth();
  const { vendor, loading } = useVendor();
  const location = useLocation();

  if (!authLoading && !user) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <SiteHeader />
        <StoreSubnav />
        <main className="flex-1 mx-auto w-full max-w-7xl px-6 py-10">
          <Skeleton className="h-8 w-48" />
          <div className="mt-6 grid grid-cols-12 gap-6">
            <Skeleton className="col-span-3 h-64" />
            <Skeleton className="col-span-9 h-64" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!vendor) {
    return <Navigate to="/store/vendor-setup" replace />;
  }

  const isSandbox = !!(vendor.metadata as Record<string, unknown> | null)?.sandbox;

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <StoreSubnav />
      <main className="flex-1 mx-auto w-full max-w-7xl px-6 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Store className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold leading-tight">{vendor.display_name}</h1>
              <p className="text-xs text-muted-foreground">Vendor portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSandbox && (
              <Badge variant="outline" className="border-primary/40 text-primary">
                <Sparkles className="h-3 w-3 mr-1" /> SANDBOX
              </Badge>
            )}
            <Badge variant={vendor.status === "active" ? "default" : "secondary"}>
              {vendor.status.replace(/_/g, " ")}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          <aside className="col-span-12 md:col-span-3 lg:col-span-2">
            <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </aside>
          <section className="col-span-12 md:col-span-9 lg:col-span-10 min-w-0">
            <Outlet />
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default VendorPortalLayout;
